import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Avatar,
  Button,
  IconButton,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import {
  FiBookmark,
  FiMessageCircle,
  FiTrash2,
  FiArrowUpRight,
} from "react-icons/fi";
import { useSession } from "./session";
import CharacterCard from "./CharacterCard";
import Pagination from "./Pagination";
export default function LibraryPage({ saved = false }) {
  const { auth, api, libraryVersion, refreshLibrary } = useSession(),
    toast = useToast(),
    [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [items, setItems] = useState([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [hasMore, setHasMore] = useState(false),
    [reload, setReload] = useState(0),
    [deleting, setDeleting] = useState("");
  useEffect(() => {
    if (!auth.isAuthenticated || auth.isLoading) return;
    const abort = new AbortController();
    setLoading(true);
    setError("");
    api(`/api/${saved ? "favorites" : "conversations"}?page=${page}`, {
      signal: abort.signal,
    })
      .then((r) => r.json())
      .then((d) => {
        setItems(d.results);
        setHasMore(d.hasMore);
      })
      .catch((e) => {
        if (e.name !== "AbortError") setError(e.message);
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [
    api,
    auth.isAuthenticated,
    auth.isLoading,
    page,
    saved,
    libraryVersion,
    reload,
  ]);
  const remove = async (item) => {
    if (
      !window.confirm(
        `Delete “${item.title}” and its scene images? This cannot be undone.`,
      )
    )
      return;
    setDeleting(item.id);
    try {
      await api(`/api/conversations/${item.id}`, { method: "DELETE" });
      refreshLibrary();
    } catch (e) {
      toast({
        title: "Could not delete chat",
        description: e.message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setDeleting("");
    }
  };
  const Icon = saved ? FiBookmark : FiMessageCircle;
  return (
    <div className="discover-page library-page">
      <div className="page-heading">
        <p className="eyebrow">YOUR LIBRARY</p>
        <h1>{saved ? "Saved characters" : "Your conversations"}</h1>
        <p>
          {saved
            ? "Keep your next story close."
            : "Pick up where you left off, on any device."}
        </p>
      </div>
      {!auth.isAuthenticated ? (
        <div className="empty-state">
          <Icon />
          <h2>Make room for your stories</h2>
          <p>Sign in to sync your chats and saved characters across devices.</p>
          <Button variant="brand" onClick={() => auth.login()}>
            Log in
          </Button>
        </div>
      ) : error ? (
        <div className="empty-state" role="alert">
          <h2>Could not sync your library</h2>
          <p>{error}</p>
          <Button onClick={() => setReload((v) => v + 1)}>Try again</Button>
        </div>
      ) : loading ? (
        <div className="empty-state">
          <Spinner />
        </div>
      ) : !items.length ? (
        <div className="empty-state">
          <Icon />
          <h2>
            {saved
              ? "A place for your favorites"
              : "Your first story is waiting"}
          </h2>
          <p>
            {saved
              ? "Tap the bookmark on a character to save them here."
              : "Start a conversation and it will appear here."}
          </p>
          <Button as={Link} to="/" variant="brand">
            Discover characters
          </Button>
        </div>
      ) : (
        <>
          <div className={saved ? "character-grid" : "conversation-list"}>
            {items.map((item, i) =>
              saved ? (
                <CharacterCard key={item.publicId} character={item} index={i} />
              ) : (
                <article className="conversation-row" key={item.id}>
                  <Link
                    to={`/chat/${item.character.publicId}?conversation=${item.id}`}
                  >
                    <Avatar
                      src={item.character.thumb}
                      name={item.character.name}
                      size="lg"
                    />
                    <span className="conversation-summary">
                      <strong>{item.character.name}</strong>
                      <span>{item.title}</span>
                      <small>{item.preview}</small>
                    </span>
                    <time>{new Date(item.updatedAt).toLocaleDateString()}</time>
                    <FiArrowUpRight aria-hidden="true" />
                  </Link>
                  <IconButton
                    aria-label={`Delete ${item.title}`}
                    icon={<FiTrash2 />}
                    variant="ghost"
                    isLoading={deleting === item.id}
                    onClick={() => remove(item)}
                  />
                </article>
              ),
            )}
          </div>
          <Pagination
            page={page}
            hasMore={hasMore}
            onPageChange={(p) => {
              setParams({ page: p });
              window.scrollTo(0, 0);
            }}
          />
        </>
      )}
    </div>
  );
}
